import { useEffect, useRef, useState } from 'react';
import {
  listBacklogIssueMeta,
  type ListBacklogIssueMetaFilter,
  type ListBacklogIssueMetaResult,
} from '../lib/backlogSearch';

export interface UseBacklogIssueMetaState {
  data: ListBacklogIssueMetaResult | null;
  loading: boolean;
  error: Error | null;
}

/** Debounce window for filter-driven re-fetches. Picked to feel snappy
 *  on keyboard navigation through dropdowns without burning callable
 *  budget on multi-axis clicks. */
const FILTER_DEBOUNCE_MS = 250;

/**
 * Fetches the filter master data (statuses / users / categories /
 * milestones / priorities) for the realtime inbox. Re-fires whenever
 * the cascade `filter` changes (debounced) so each axis's dropdown
 * options stay narrowed to what's actually probe-visible under the
 * current selection.
 *
 * Previous successful data is kept on screen while a new fetch is in
 * flight — without this, the filter chips would flash empty between
 * every selection change.
 */
export function useBacklogIssueMeta(
  projectId: string | null,
  filter?: ListBacklogIssueMetaFilter,
): UseBacklogIssueMetaState {
  const [data, setData] = useState<ListBacklogIssueMetaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Stringify so structurally-equal filters don't re-trigger fetches.
  // Aligns with useBacklogSearch's filter-key strategy.
  const filterKey = JSON.stringify(filter ?? {});
  const filterRef = useRef(filter ?? {});
  filterRef.current = filter ?? {};
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await listBacklogIssueMeta({
            projectId,
            filter: filterRef.current,
          });
          if (reqId !== reqIdRef.current) return;
          setData(result);
        } catch (err) {
          if (reqId !== reqIdRef.current) return;
          setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
          if (reqId === reqIdRef.current) setLoading(false);
        }
      })();
    }, FILTER_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
    // We intentionally key on filterKey (stringified) so identity-only
    // changes don't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filterKey]);

  return { data, loading, error };
}
